import { Component, OnInit, Input, OnDestroy, Output, ViewChild, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription, Subject, EMPTY } from 'rxjs';
import { map, switchMap, debounceTime, filter, takeUntil, catchError } from 'rxjs/operators';
import { ClientApiService } from 'src/app/services/client-api.service';

import { Document } from 'src/app/shared/document';

/**
 * @description
 * Renders the buyer-side (client / PCO) party panel of an edit-document form.
 * Manages two independent party groups — the direct client (`CLI`) and the legal
 * entity of the buyer (`PCO`) — and keeps their depot/contact fields in sync.
 *
 * When the user selects a buyer company (`cliCompanySelected`), the component emits
 * the selected id through the internal `clientId$` Subject. A debounced `switchMap`
 * calls `ClientApiService.getMainIdByClientId()`, resolves the PCO counterpart, and
 * patches `document.DOCCLIENTSID` / `document.DOCPCOCLIID` before emitting `changes`.
 *
 * When a stock depot changes (`cliStockSelected` / `pcoCompanySelected`), the component
 * resolves the matching child department via `ClientApiService.getChildDepartmentsById()`
 * and patches `document.DOCPCODEPID`.
 *
 * All subscriptions are torn down via `takeUntil(destroy$)`. Errors inside `switchMap`
 * are caught with `catchError` (returning `EMPTY`) to keep the outer stream alive.
 *
 * ### Inputs / Outputs
 * | Member         | Type              | Description                                  |
 * |----------------|-------------------|----------------------------------------------|
 * | `document`     | Input\<Document\>   | The shared document being edited             |
 * | `cliCliLabel`  | Input\<string\>     | Label for client company selector            |
 * | `cliPerLabel`  | Input\<string\>     | Label for client contact selector            |
 * | `cliDepLabel`  | Input\<string\>     | Label for client stock selector              |
 * | `pcoCliLabel`  | Input\<string\>     | Label for PCO company selector               |
 * | `pcoPerLabel`  | Input\<string\>     | Label for PCO contact selector               |
 * | `pcoDepLabel`  | Input\<string\>     | Label for PCO stock selector                 |
 * | `changes`      | Output\<Document\>  | Emitted after any document field mutation    |
 */
@Component({
  selector: 'app-edit-document-client',
  templateUrl: './edit-document-client.component.html',
  styleUrls: ['./edit-document-client.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentClientComponent implements OnInit, OnChanges, OnDestroy {

  @Input() document: Document;

  @Input() cliCliLabel: string = 'Основная организация покупателя';
  @Input() cliPerLabel: string = 'Контактное лицо';
  @Input() cliDepLabel: string = 'Основной склад';
  @Input() pcoCliLabel: string = 'Юридическое лицо покупателя';
  @Input() pcoPerLabel: string = 'Ответственный';
  @Input() pcoDepLabel: string = 'Склад';

  cliCompanyId: number;
  private clientId$ = new Subject<number>();
  private _depChildQuery: Subscription;
  private _mainIdQuery: Subscription;
  private destroy$ = new Subject<void>();

  @Output() changes: EventEmitter<Document> = new EventEmitter();

  constructor(
    private cliApi: ClientApiService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** EditDocumentClientComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentClientComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //console.log('*** EditDocumentClientComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {

        case 'document':

          if (this.cliCompanyId !== change.currentValue.DOCCLIENTSID) {
            this.cliCompanyId = change.currentValue.DOCCLIENTSID;
          }

          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('*** EditDocumentClientComponent in ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    console.log('*** EditDocumentClientComponent in ngOnInit');

    this._mainIdQuery =
      this.clientId$.pipe(
        debounceTime(200),
//        distinctUntilChanged(),
        switchMap((id: number) => {
          //console.log('*** CompanyselectorComponent in switchMap: pgn=(' + id + ')');

          return this.cliApi.getMainIdByClientId(id)
            .pipe(
              map(mc => {
                console.log('*** EditDocumentClientComponent in clientId$ pipe/map: ' + JSON.stringify(mc));
                this.document.DOCCLIENTSID = (mc && mc[0]) ? mc[0].CLIID : null;
                this.document.DOCPCOCLIID = id;
                this.changes.emit(this.document);
              }),
              catchError(err => {
                console.error('*** EditDocumentClientComponent _mainIdQuery error:', err);
                return EMPTY;
              })
            );
        }),
        takeUntil(this.destroy$)
      )
        .subscribe();
  }

  /**
   * @description
   * Called when the user selects a buyer (CLI) company. Pushes the selected id into
   * the internal `clientId$` subject, triggering the debounced `switchMap` pipeline
   * that resolves `DOCCLIENTSID` / `DOCPCOCLIID` and emits `changes`.
   */
  cliCompanySelected(selectedId) {
    console.log('*** EditDocumentClientComponent in cliCompanySelected: ' + selectedId);

    this.clientId$.next(selectedId);
  }

  /**
   * @description
   * Called when the user selects a contact person for the CLI group. Writes `selectedId`
   * to `document.DOCPERSONID`. If the CLI and PCO company IDs are equal, also mirrors
   * the person and depot into the PCO fields (`DOCPCOPERID`, `DOCPCODEPID`). Emits changes.
   */
  cliPersonSelected(selectedId) {
    /*
     * TODO: Select the EditDocumentClientComponent person in PCO group (have right to sign the doc)
     */
    console.log('*** EditDocumentClientComponent in cliPersonSelected: ' + selectedId);
    this.document.DOCPERSONID = selectedId;
    if (this.document.DOCCLIENTSID == this.document.DOCPCOCLIID) {
      this.document.DOCPCOPERID = this.document.DOCPERSONID;
      this.document.DOCPCODEPID = this.document.DOCCLIDEPID;
    }
    this.changes.emit(this.document);
  }

  /**
   * @description
   * Called when the user selects a stock depot for the CLI group. Sets `document.DOCCLIDEPID`,
   * then resolves the matching PCO child department via
   * `ClientApiService.getChildDepartmentsById()` and patches `document.DOCPCODEPID`.
   * Emits changes on success. Uses `takeUntil(destroy$)` for teardown.
   */
  cliStockSelected(selectedId) {
    /*
     * TODO: Select the corresponding department in PCO group (equal name)
     */
    console.log('*** EditDocumentClientComponent in cliStockSelected: ' + selectedId);
    this.document.DOCCLIDEPID = selectedId;

    //if (this.document.DOCCLIENTSID == this.document.DOCPCOCLIID) {
    //  this.document.DOCPCODEPID = this.document.DOCCLIDEPID;
    //  this.changes.emit(this.document);
    //}

    this._depChildQuery =
      this.cliApi.getChildDepartmentsById(this.document.DOCCLIDEPID, this.document.DOCPCOCLIID)
      .pipe(
        map(data => this.document.DOCPCODEPID = data[0].CLIID),
        takeUntil(this.destroy$)
      )
        .subscribe({
          next: () => { this.changes.emit(this.document); },
          error: err => console.error('*** EditDocumentClientComponent cliStockSelected _depChildQuery error:', err)
        });
  }

  /**
   * @description
   * Called when the user selects a PCO (legal entity of buyer) company. Sets
   * `document.DOCPCOCLIID`, then resolves the matching child department via
   * `ClientApiService.getChildDepartmentsById()` and patches `document.DOCPCODEPID`.
   * Emits changes on success. Skips the query result if the returned list is empty.
   * Uses `takeUntil(destroy$)` for teardown.
   */
  pcoCompanySelected(selectedId) {
    console.log('*** EditDocumentClientComponent in pcoCompanySelected: ' + selectedId);
    this.document.DOCPCOCLIID = selectedId;

    this._depChildQuery =
      this.cliApi.getChildDepartmentsById(this.document.DOCCLIDEPID, this.document.DOCPCOCLIID)
      .pipe(
        filter(data => data.length > 0),
        map(data => this.document.DOCPCODEPID = data[0].CLIID),
        takeUntil(this.destroy$)
      )
        .subscribe({
          next: () => { this.changes.emit(this.document); },
          error: err => console.error('*** EditDocumentClientComponent pcoCompanySelected _depChildQuery error:', err)
        });

    //this.changes.emit(this.document);
  //  this.document.DOCPCOPERID = null;
  //  this.document.DOCPCODEPID = null;
  }

  /** @description Called when the user selects a contact person for the PCO entity. Writes `selectedId` to `document.DOCPCOPERID` and emits changes. */
  pcoPersonSelected(selectedId) {
    console.log('*** EditDocumentClientComponent in pcoPersonSelected: ' + selectedId);
    this.document.DOCPCOPERID = selectedId;
    this.changes.emit(this.document);
  }

  /** @description Called when the user selects a stock depot for the PCO entity. Writes `selectedId` to `document.DOCPCODEPID` and emits changes. */
  pcoStockSelected(selectedId) {
    console.log('*** EditDocumentClientComponent in pcoStockSelected: ' + selectedId);
    this.document.DOCPCODEPID = selectedId;
    this.changes.emit(this.document);
  }

}
